import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TooltipDirective } from '../../auxiliares/tooltip/tooltip.directive';
import { BotonIconoComponent } from '../botones';

/**
 * Envuelve contenido horizontal (ej. un irx-segmentado) con flechas
 * de desplazamiento a los costados. Mismo lenguaje visual que la paginación
 * nativa de mat-tab-header (el "< Viajes  Zona  >" de arriba): flechas
 * chevron simples con borde, no un botón circular con ícono.
 *
 * Uso:
 * <app-horizontal-scroller>
 *   <irx-segmentado>...</irx-segmentado>
 * </app-horizontal-scroller>
 */
@Component({
  selector: 'app-horizontal-scroller',
  standalone: true,
  imports: [MatIconModule, BotonIconoComponent, TooltipDirective],
  templateUrl: './horizontal-scroller.component.html',
  styleUrls: ['./horizontal-scroller.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HorizontalScrollerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('content') private contentRef!: ElementRef<HTMLElement>;

  public hasOverflow = false;
  public canScrollLeft = false;
  public canScrollRight = false;

  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;

  public ngAfterViewInit(): void {
    const el = this.contentRef.nativeElement;
    this.resizeObserver = new ResizeObserver(() => this.updateState());
    this.resizeObserver.observe(el);

    // Si esto vive dentro de una mat-tab con [preserveContent]="true",
    // Material puede montarlo mientras su pestaña todavía está oculta (mide
    // 0 y ResizeObserver no vuelve a disparar solo). IntersectionObserver
    // sí avisa cuando pasa de oculto a visible al activarse la pestaña.
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.updateState();
    });
    this.intersectionObserver.observe(el);

    // El contenido (ej. irx-segmentado con mat-icon) puede cambiar
    // de ancho después del primer render: la tipografía de los íconos carga
    // async y ResizeObserver no dispara acá porque el ancho de el (100% del
    // padre) no cambia, aunque el contenido que desborda sí lo haga.
    requestAnimationFrame(() => this.updateState());
    document.fonts?.ready.then(() => this.updateState());
    setTimeout(() => this.updateState(), 300);
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  public onScroll(): void {
    this.updateState();
  }

  public scroll(direction: 1 | -1): void {
    const el = this.contentRef.nativeElement;
    el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: 'smooth' });
    // El listener (scroll) del template va actualizando las flechas cuadro a
    // cuadro mientras dura la animación; esto solo evita que, si el scroll
    // no dispara eventos de inmediato, quede un estado viejo hasta que sí lo
    // haga.
    this.updateState();
  }

  private updateState(): void {
    const el = this.contentRef.nativeElement;
    // Tolerancia de 2px: con anchos fraccionarios (panel redimensionable, zoom
    // del navegador) scrollWidth y clientWidth redondean distinto y un sobrante
    // de 1px alcanzaba para mostrar las flechas sin que falte lugar de verdad.
    this.hasOverflow = el.scrollWidth > el.clientWidth + 2;
    this.canScrollLeft = el.scrollLeft > 1;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }
}
